import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './SezionaliIvaPage.api';
import './SezionaliRegistri.css';

const MODULO = 'amministrazione.sezionali_iva';

interface SezionaleIva {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  isDefault: boolean;
  ultimoNumero: number;
  ultimoAnno: number;
  attivo: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ModalState {
  mode: 'create' | 'edit';
  record: SezionaleIva | null;
}

interface FormState {
  codice: string;
  descrizione: string;
  isDefault: boolean;
  note: string;
}

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function SezionaliIvaPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [items, setItems] = useState<SezionaleIva[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);
  const [ricerca, setRicerca] = useState('');

  const [modal, setModal] = useState<ModalState | null>(null);
  const [form, setForm] = useState<FormState>({ codice: '', descrizione: '', isDefault: false, note: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/sezionali-iva?societaId= -> { success: true, data: [{ id, societaId, codice, descrizione, isDefault, ultimoNumero, ultimoAnno, attivo, note }] }
      const res = await apiFetch<{ success: boolean; data: SezionaleIva[] }>(`/api/sezionali-iva?societaId=${societaId}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return items.filter(s => {
      if (!mostraDisattivi && !s.attivo) return false;
      if (q && !(s.codice.toLowerCase().includes(q) || s.descrizione.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, ricerca, mostraDisattivi]);

  const openCreate = () => {
    setModal({ mode: 'create', record: null });
    setForm({ codice: '', descrizione: '', isDefault: false, note: '' });
    setFormError('');
  };

  const openEdit = (r: SezionaleIva) => {
    setModal({ mode: 'edit', record: r });
    setForm({ codice: r.codice, descrizione: r.descrizione, isDefault: r.isDefault, note: r.note || '' });
    setFormError('');
  };

  const closeModal = () => { setModal(null); setFormError(''); };

  const salva = async () => {
    if (!modal) return;
    const codice = form.codice.trim();
    const descrizione = form.descrizione.trim();
    if (!codice) { setFormError('Codice obbligatorio'); return; }
    if (!/^[A-Za-z0-9_-]+$/.test(codice)) { setFormError('Il codice può contenere solo lettere, numeri, "-" e "_"'); return; }
    if (!descrizione) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      if (modal.mode === 'create') {
        // @api POST /api/sezionali-iva { societaId, codice, descrizione, isDefault, note } -> { success: true, data: { id } }
        await apiFetch('/api/sezionali-iva', {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(societaId),
            codice,
            descrizione,
            isDefault: form.isDefault,
            note: form.note.trim() || null,
          }),
        });
      } else if (modal.record) {
        // @api PUT /api/sezionali-iva/:id { codice, descrizione, note } -> { success: true, data: { id } }
        await apiFetch(`/api/sezionali-iva/${modal.record.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            codice,
            descrizione,
            note: form.note.trim() || null,
          }),
        });
        // Se il flag default è cambiato, lo gestisco con PATCH dedicato
        if (form.isDefault !== modal.record.isDefault) {
          // @api PATCH /api/sezionali-iva/:id/default { isDefault } -> { success: true, data: { id } }
          await apiFetch(`/api/sezionali-iva/${modal.record.id}/default`, {
            method: 'PATCH',
            body: JSON.stringify({ isDefault: form.isDefault }),
          });
        }
      }
      closeModal();
      load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleDefault = async (r: SezionaleIva) => {
    try {
      await apiFetch(`/api/sezionali-iva/${r.id}/default`, {
        method: 'PATCH',
        body: JSON.stringify({ isDefault: !r.isDefault }),
      });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nell\'impostazione del default');
    }
  };

  const toggleAttivo = async (r: SezionaleIva) => {
    try {
      // @api PATCH /api/sezionali-iva/:id/stato { attivo } -> { success: true, data: { id } }
      await apiFetch(`/api/sezionali-iva/${r.id}/stato`, {
        method: 'PATCH',
        body: JSON.stringify({ attivo: !r.attivo }),
      });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nel cambio stato');
    }
  };

  const elimina = async (r: SezionaleIva) => {
    if (!window.confirm(`Eliminare il sezionale ${r.codice} — ${r.descrizione}?`)) return;
    try {
      // @api DELETE /api/sezionali-iva/:id -> { success: true }
      await apiFetch(`/api/sezionali-iva/${r.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  const noSocieta = !societaId;

  return (
    <Layout>
      <div className="sezionali-registri">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Sezionali IVA</h2>
            <p>Registri IVA della società, identificati da codice e descrizione. Uno può essere marcato come default.</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-primary" onClick={openCreate} disabled={noSocieta}>
                + Nuovo sezionale IVA
              </button>
            </div>
          )}
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <input
            className="bop-input sr-search"
            placeholder="Cerca per codice o descrizione…"
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <label className="bop-flex sr-checkbox-label">
            <input type="checkbox" checked={mostraDisattivi} onChange={e => setMostraDisattivi(e.target.checked)} />
            <span>Mostra anche disattivi</span>
          </label>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per gestire i suoi sezionali IVA.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="bop-empty-state">
                Nessun sezionale IVA configurato.{canEdit ? ' Crea il primo (es. "VND" — Vendite, "ACQ" — Acquisti).' : ''}
              </div>
            ) : filtered.length === 0 ? (
              <div className="bop-empty-state">Nessun risultato per i filtri impostati.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th className="bop-text-center">Default</th>
                    <th>Numerazione corrente</th>
                    <th>Stato</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className={r.attivo ? '' : 'sr-row--inactive'}>
                      <td className="sr-codice">{r.codice}</td>
                      <td>
                        <div className="sr-descrizione-cell">
                          <span>{r.descrizione}</span>
                          {r.note && <span className="sr-note">{r.note}</span>}
                        </div>
                      </td>
                      <td className="bop-text-center">
                        <button
                          type="button"
                          className={`sr-star ${r.isDefault ? 'sr-star--active' : ''}`}
                          onClick={() => canEdit && toggleDefault(r)}
                          disabled={!canEdit || !r.attivo}
                          title={r.isDefault ? 'Sezionale di default' : 'Imposta come sezionale di default'}
                        >
                          <StarIcon filled={r.isDefault} />
                        </button>
                      </td>
                      <td>
                        {r.ultimoNumero > 0
                          ? <span className="sr-numerazione">{r.ultimoNumero}/{r.ultimoAnno}</span>
                          : <span className="bop-text-muted">non ancora utilizzato</span>}
                      </td>
                      <td>
                        <span className={`bop-badge ${r.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
                          {r.attivo ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEdit(r), hidden: !canEdit },
                          { icon: <RowIcon.Disable />, label: r.attivo ? 'Disattiva' : 'Riattiva', onClick: () => toggleAttivo(r), hidden: !canEdit },
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(r), variant: 'danger', hidden: !canDelete || r.ultimoNumero > 0 },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <Modal
          title={modal.mode === 'edit'
            ? `Modifica sezionale IVA ${modal.record?.codice ?? ''}`.trim()
            : 'Nuovo sezionale IVA'}
          onClose={closeModal}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Codice *</label>
              <input
                className="bop-input"
                value={form.codice}
                onChange={e => setForm(f => ({ ...f, codice: e.target.value }))}
                placeholder="es. VND, VND01, ACQ-IT"
                autoFocus
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Descrizione *</label>
              <input
                className="bop-input"
                value={form.descrizione}
                onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
                placeholder="es. Vendite Italia, Acquisti UE, Corrispettivi punto vendita…"
              />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-flex sr-checkbox-label">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
              />
              <span>Imposta come sezionale di default</span>
            </label>
            <p className="sr-hint">
              Il default è usato automaticamente dalle scritture/fatture che non specificano un sezionale.
              Se ne marchi uno nuovo, l'eventuale default precedente viene rimosso.
            </p>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
            />
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
